import { useState } from "react";
import { CreditCard, Layers, Loader2, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { btnGold, btnGhost, errMsg, fmtDate } from "./ui";

const LABEL = {
  trialing: "Free trial", active: "Active", complimentary: "Complimentary access", grace: "Payment overdue — grace period",
  none: "No subscription", canceled: "Cancelled", past_due: "Payment overdue", unpaid: "Unpaid", incomplete: "Incomplete",
};
const SINGLE = { attendance: "attendance", projection: "hymn_projection" };
const NAME = { attendance: "HOH Attendance", projection: "HOH Worship Projection" };
const money = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function SubscriptionBanner({ org, plan, product = "attendance", returnPath = "/attendance/dashboard", canBill = true }) {
  const [busy, setBusy] = useState(null);
  const a = product === "projection" ? org.projection_access : org.access;
  const plans = Object.fromEntries((plan?.plans || []).map((p) => [p.id, p]));
  const single = plans[SINGLE[product]];
  const bundle = plans.church_bundle;

  const go = async (kind, interval = "monthly", planId = SINGLE[product]) => {
    setBusy(kind + interval + planId);
    try {
      const path = kind === "portal" ? "/attendance/billing/portal" : "/attendance/billing/checkout";
      const upgradeInterval = planId === "church_bundle" && a.reason === "not_included" ? (org.subscription_interval || "monthly") : interval;
      const r = await api.post(path, { interval: kind === "checkout" ? upgradeInterval : interval, origin_url: window.location.origin, plan_id: planId, return_path: returnPath });
      if (r.data.changed) { toast.success("Subscription upgraded to the Bundle"); window.location.reload(); return; }
      window.location.assign(r.data.url);
    } catch (e) { toast.error(errMsg(e, "Billing is unavailable right now")); setBusy(null); }
  };

  if (a.has_access) {
    return (
      <div data-testid="att-sub-banner" className="luxury-card rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Sparkles size={16} className="text-[#D4AF37]" />
          <span className="text-white" data-testid="att-sub-status">{LABEL[a.reason] || a.reason}</span>
          {org.subscription_plan === "church_bundle" && a.reason !== "complimentary" && <span className="text-[10px] tracking-widest uppercase border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5 rounded" data-testid="sub-bundle-badge">Bundle</span>}
          {a.until && (a.until.startsWith("9999") ? <span className="text-neutral-400" data-testid="att-sub-permanent">· permanent</span> : <span className="text-neutral-400">· {a.reason === "trialing" ? "first charge on" : "until"} {fmtDate(a.until)}</span>)}
          {org.cancel_at_period_end && <span className="text-amber-300">· cancels at period end</span>}
          {a.reason === "trialing" && !org.cancel_at_period_end && <span className="text-neutral-500 text-xs" data-testid="att-cancel-terms">· cancel before then and you won't be charged</span>}
        </div>
        {canBill && org.subscription_status && org.subscription_status !== "none" && (
          <button onClick={() => go("portal")} disabled={!!busy} data-testid="att-billing-portal" className={btnGhost}><CreditCard size={14} /> Manage billing</button>
        )}
      </div>
    );
  }

  if (!canBill) {
    return (
      <div data-testid="att-sub-banner" className="rounded-xl p-5 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] text-sm text-neutral-300">
        <span data-testid="att-sub-status">{NAME[product]} isn't active for {org.name}.</span> Please ask the church account owner ({org.owner_email}) to start the subscription.
      </div>
    );
  }

  if (a.reason === "not_included" && bundle) {
    return (
      <div data-testid="att-sub-banner" className="rounded-xl p-6 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]">
        <div className="flex items-start gap-3">
          <Layers className="text-[#D4AF37] shrink-0" size={20} />
          <div className="flex-1">
            <div className="text-white font-medium" data-testid="att-sub-status">Add {NAME[product]} to your subscription</div>
            <p className="text-sm text-neutral-300 mt-1">Your current subscription doesn't include {NAME[product]}. Upgrade to the {bundle.name} — {money(bundle.price_monthly)}/month or {money(bundle.price_annual)}/year. You won't pay for two subscriptions: the difference is prorated on your existing billing cycle.</p>
            <button onClick={() => go("checkout", "monthly", "church_bundle")} disabled={!!busy} data-testid="sub-upgrade-bundle" className={`${btnGold} mt-4`}>
              {busy && <Loader2 size={14} className="animate-spin" />} Upgrade to Bundle
            </button>
          </div>
        </div>
      </div>
    );
  }

  const trial = org.trial_available ? org.trial_available[SINGLE[product]] : !org.trial_used;
  return (
    <div data-testid="att-sub-banner" className="rounded-xl p-6 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]">
      <div className="flex items-start gap-3">
        <ShieldAlert className="text-[#D4AF37] shrink-0" size={20} />
        <div className="flex-1">
          <div className="text-white font-medium" data-testid="att-sub-status">{trial ? `Start your ${plan?.trial_days || 30}-day free trial` : `Subscription ${LABEL[a.reason] || "inactive"}`}</div>
          <p className="text-sm text-neutral-300 mt-1">
            {trial
              ? `Add a card to start. Nothing is charged for ${plan?.trial_days || 30} days; then the plan you choose renews automatically unless you cancel.`
              : product === "projection" ? "Hymn projection is paused until you subscribe." : "Counting and new events are paused. Your organisation data and past reports remain available."}
          </p>
          <PlanButtons p={single} id={SINGLE[product]} go={go} busy={busy} primary testPrefix="att-checkout" />
          {bundle && <PlanButtons p={bundle} id="church_bundle" go={go} busy={busy} testPrefix="sub-bundle" note="Attendance + Hymn Projection together" />}
          {plan?.billing_mode !== "live" && <p className="text-[11px] text-neutral-500 mt-3" data-testid="att-billing-mode">Billing mode: {plan?.billing_mode === "test" ? "Stripe test mode (no real charges)" : "not yet configured"}</p>}
        </div>
      </div>
    </div>
  );
}

function PlanButtons({ p, id, go, busy, primary, testPrefix, note }) {
  if (!p) return null;
  const b = (iv) => busy === `checkout${iv}${id}`;
  return (
    <div className="mt-4">
      <div className="text-[11px] tracking-widest uppercase text-neutral-400 mb-2">{p.name}{note && <span className="normal-case tracking-normal text-neutral-500"> · {note}</span>}</div>
      <div className="flex flex-wrap gap-3">
        <button onClick={() => go("checkout", "monthly", id)} disabled={!!busy} data-testid={`${testPrefix}-monthly`} className={primary ? btnGold : btnGhost}>
          {b("monthly") && <Loader2 size={14} className="animate-spin" />} Monthly · {money(p.price_monthly)}
        </button>
        <button onClick={() => go("checkout", "annual", id)} disabled={!!busy} data-testid={`${testPrefix}-annual`} className={btnGhost}>
          {b("annual") && <Loader2 size={14} className="animate-spin" />} Annual · {money(p.price_annual)}
        </button>
      </div>
    </div>
  );
}
